/**
 * Automations Page
 *
 * Main page for managing automations. Displays a list of all automations
 * and allows creation, editing, cloning, and deletion.
 */

import { faFolderPlus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  type UseConfirmDialogReturn,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Loader } from 'src/components/Loader';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { AutomationLayout } from 'src/models/automation-group/_types';
import {
  automationGroupsQueryKey,
  automationsQueryKey,
  buildAutomationLayout,
  createAutomationGroup,
  deleteAutomationGroup,
  fetchAutomationGroups,
  flattenAutomationLayout,
  patchReminderInAutomationsCache,
  patchRemindersAfterGroupDelete,
  patchRemindersAfterGroupUpdate,
  patchRemindersWithMissingGroups,
  updateAutomationGroup,
} from 'src/models/automation-group/automation-group';
import { Reminder } from 'src/models/reminder/_types';
import {
  createReminder,
  deleteReminder,
  fetchReminders,
  updateReminder,
} from 'src/models/reminder/reminder';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { AutomationCard } from 'src/shared/Automation/AutomationCard';
import {
  AUTOMATION_ACTIONS,
  getAutomationAction,
} from 'src/shared/Automation/automationActions';
import { TRIGGERS } from 'src/shared/Automation/automationTriggers';
import { AutomationItem, FilterGroup } from 'src/shared/Automation/types';
import { Filters, SelectFilter } from 'src/shared/Filters/Filters';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { Meta } from '../../components/Meta';

import { AutomationGroupHeaderActions } from './AutomationGroupHeaderActions';
import { AutomationLayoutDnD } from './AutomationLayoutDnD';
import { AutomationGroupDialogValues, CreateAutomationGroupDialog } from './CreateAutomationGroupDialog';
import { CreateAutomationWizard } from './CreateAutomationWizard';
import { automationDragCardSx } from './automationDragStyles';

interface AutomationsFormValues {
  automations: AutomationItem[];
}

const DEFAULT_AUTOMATION: Partial<AutomationItem> = {
  reminderType: '',
  delayInterval: 0,
  delayType: 'day',
  media: ['email'],
  filters: { condition: 'AND', filters: [] },
  content: [],
  tags: '',
  count: 1,
  trainerId: 0,
  sendToIds: [],
  reportFilters: [],
  enabled: true,
  automationGroupId: null,
  position: 0,
};

const AUTOMATION_TYPE_DEFAULTS: Record<string, Partial<AutomationItem>> = {
  custom: {
    delayInterval: 1,
    delayType: 'day',
    media: ['email'],
    reminderType: 'workout',
  },
  'close-out-visit': {
    content: [],
    delayInterval: 1,
    delayType: 'day',
  },
  'workout-today': {
    delayInterval: 8,
    delayType: 'hour',
    media: ['email'],
  },
  'client-tag': {
    reminderType: 'assessment',
    delayInterval: 0,
    delayType: 'hour',
  },
  message: {
    reminderType: 'client-signup',
    media: ['message'],
    delayType: 'day',
    delayInterval: 0,
  },
  'upcoming-appointment': {
    reminderType: 'upcoming-appointment',
    delayType: 'hour',
    delayInterval: 48,
  },
  'visits-left': {
    reminderType: 'visits-left',
    count: 1,
    media: ['email'],
  },
  'cancel-user': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'cancel_subscription',
  },
  'clear-modal-alert': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'successful-payment',
  },
};

const AUTOMATION_TYPE_OPTIONS = Object.entries(AUTOMATION_ACTIONS)
  .map(([key, config]) => ({
    value: key,
    label: config.label,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

function normalizeAutomationFilters(filters: unknown): FilterGroup {
  if (
    filters &&
    typeof filters === 'object' &&
    !Array.isArray(filters) &&
    'condition' in filters &&
    Array.isArray((filters as FilterGroup).filters)
  ) {
    return filters as FilterGroup;
  }

  return { condition: 'AND', filters: [] };
}

function normalizeAutomations(data: Reminder[]): AutomationItem[] {
  return data.map((r) => ({
    ...r,
    automationGroupId: r.automationGroupId ?? null,
    position: r.position ?? 0,
    content: Array.isArray(r.content) ? r.content : (r.content ? [r.content] : []),
    filters: normalizeAutomationFilters(r.filters),
  }));
}

const findAutomationIndex = (automations: AutomationItem[], reminderId?: Identifier) =>
  automations.findIndex(
    (automation) =>
      reminderId !== undefined &&
      automation.id !== undefined &&
      String(automation.id) === String(reminderId)
  );

function AutomationsFormContent({
  automationLayout,
  confirm,
  customTypeFilter,
  deleteMutation,
  formRef,
  onDeleteGroup,
  onUpdateGroup,
  reminders,
  resetKey,
  setResetKey,
  createMutation,
  updateMutation,
}: {
  automationLayout: AutomationLayout;
  confirm: UseConfirmDialogReturn[1];
  customTypeFilter: string | undefined;
  deleteMutation: {
    mutateAsync: (id: number) => Promise<unknown>;
  };
  formRef: RefObject<FormikProps<AutomationsFormValues> | null>;
  onDeleteGroup: (groupId: Identifier) => Promise<void>;
  onUpdateGroup: (groupId: Identifier, values: AutomationGroupDialogValues) => Promise<void>;
  reminders: Reminder[];
  resetKey: number;
  setResetKey: Dispatch<SetStateAction<number>>;
  createMutation: {
    mutateAsync: (automation: Reminder) => Promise<Reminder>;
  };
  updateMutation: {
    mutateAsync: (automation: Partial<Reminder>) => Promise<Reminder>;
  };
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [savingAutomationId, setSavingAutomationId] = useState<Identifier | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<Identifier | null>(null);
  const { values, setFieldValue, setValues } =
    useFormikContext<AutomationsFormValues>();
  const automationsRef = useRef(values.automations);
  automationsRef.current = values.automations;
  const [togglingIndex, setTogglingIndex] = useState<number | null>(null);

  const handleToggleEnabled = async (index: number, nextEnabled: boolean) => {
    const automation = values.automations[index];
    if (!automation.id) {
      return;
    }

    const previousEnabled = automation.enabled ?? true;
    setTogglingIndex(index);
    setFieldValue(`automations.${index}.enabled`, nextEnabled);

    try {
      await updateReminder({ id: automation.id, enabled: nextEnabled });
      await queryClient.invalidateQueries({
        queryKey: ['automations', customTypeFilter],
      });
      toast.success(
        nextEnabled
          ? t('automations.common.toggleEnabledSuccess')
          : t('automations.common.toggleDisabledSuccess')
      );
    } catch {
      setFieldValue(`automations.${index}.enabled`, previousEnabled);
      toast.error(t('automations.common.toggleEnabledError'));
    } finally {
      setTogglingIndex(null);
    }
  };

  const unsavedAutomations = values.automations.filter((automation) => !automation.id);

  useEffect(() => {
    if (values.automations.length === 0 && reminders.length > 0) {
      setValues({
        automations: normalizeAutomations(reminders),
      });
    }
  }, [reminders, setValues, values.automations.length]);

  const refreshAutomations = async () => {
    await queryClient.refetchQueries({
      queryKey: automationsQueryKey(customTypeFilter),
    });
    const freshReminders =
      queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
      reminders;
    setFieldValue('automations', [
      ...unsavedAutomations,
      ...normalizeAutomations(freshReminders),
    ]);
  };

  const handleRemove = async (_index: number, automation: AutomationItem) => {
    if (!automation.id) {
      return;
    }
    const confirmed = await confirm({
      title: t('automations.page.deleteTitle'),
      message: t('automations.page.deleteConfirm'),
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(automation.id);
      await refreshAutomations();
      setResetKey((k) => k + 1);
    } catch {
      // handled by mutation
    }
  };

  const getFormAutomations = () =>
    formRef.current?.values.automations ?? automationsRef.current;

  const updateAutomationById = (
    automationId: Identifier,
    patch: Partial<AutomationItem>
  ) => {
    setFieldValue(
      'automations',
      getFormAutomations().map((automation) =>
        automation.id != null && String(automation.id) === String(automationId)
          ? { ...automation, ...patch }
          : automation
      )
    );
  };

  const findAutomationById = (automationId: Identifier | undefined) => {
    if (automationId == null) {
      return undefined;
    }

    return getFormAutomations().find(
      (automation) =>
        automation.id != null && String(automation.id) === String(automationId)
    );
  };

  const handleSaveCard = async (
    automationId: Identifier | undefined,
    index: number
  ) => {
    const automation =
      findAutomationById(automationId) ?? getFormAutomations()[index];
    if (!automation) {
      toast.error(t('automations.page.failedToUpdate'));
      return;
    }

    const reminderData = automation as unknown as Reminder;
    setSavingAutomationId(automation.id ?? `unsaved-${index}`);
    try {
      let savedReminder: Reminder;
      if (automation.id) {
        savedReminder = await updateMutation.mutateAsync(reminderData);
      } else {
        savedReminder = await createMutation.mutateAsync(reminderData);
        await refreshAutomations();
      }

      const normalizedSaved = normalizeAutomations([savedReminder])[0];
      const nextAutomations = getFormAutomations().map((value) => {
        if (
          savedReminder.id != null &&
          value.id != null &&
          String(value.id) === String(savedReminder.id)
        ) {
          return normalizedSaved;
        }
        if (!savedReminder.id && value === automation) {
          return normalizedSaved;
        }
        return value;
      });
      setValues({ automations: nextAutomations });
      setResetKey((k) => k + 1);
    } finally {
      setSavingAutomationId(null);
    }
  };

  const handleDiscardCard = (index: number) => {
    const automation = values.automations[index];
    if (!automation.id) {
      setFieldValue(
        'automations',
        values.automations.filter((_, currentIndex) => currentIndex !== index)
      );
    } else {
      const fromServer = normalizeAutomations(reminders).find(
        (item) =>
          item.id != null && String(item.id) === String(automation.id)
      );
      if (fromServer) {
        updateAutomationById(automation.id!, cloneDeep(fromServer));
      }
    }
    setResetKey((k) => k + 1);
  };

  const handleClone = (index: number, automation: AutomationItem) => {
    const cloned = {
      ...automation,
      id: undefined,
      automationGroupId: null,
      name: `${automation.name || t('automations.common.automation')} (${t('automations.page.copy')})`,
      enabled: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as AutomationItem;
    setFieldValue('automations', [cloned, ...values.automations]);
  };

  const handleReordered = (updatedLayout: AutomationLayout) => {
    queryClient.setQueryData(
      automationsQueryKey(customTypeFilter),
      flattenAutomationLayout(updatedLayout)
    );
    queryClient.setQueryData(
      automationGroupsQueryKey(),
      updatedLayout.automationGroups.map(({ reminders: _reminders, ...group }) => group)
    );

    const flattened = normalizeAutomations(flattenAutomationLayout(updatedLayout));
    const automationsById = new Map(flattened.map((automation) => [automation.id, automation]));

    setFieldValue(
      'automations',
      values.automations.map((automation) => {
        if (!automation.id || !automationsById.has(automation.id)) {
          return automation;
        }

        const updatedAutomation = automationsById.get(automation.id)!;
        return {
          ...automation,
          automationGroupId: updatedAutomation.automationGroupId,
          position: updatedAutomation.position,
        };
      })
    );
  };

  const handleReorderFailed = () => {
    toast.error(t('automations.groups.failedToReorder'));
  };

  const renderAutomationCard = (
    automation: AutomationItem,
    index: number,
    dragHandle?: ReactNode,
    compactCard = false,
    dragging = false
  ) => {
    const actionConfig = getAutomationAction(automation.customType);
    if (!actionConfig) {
      return (
        <Card>
          <Box p={2}>
            <Typography>
              {t('automations.page.unknownActionType', { type: automation.customType })}
            </Typography>
          </Box>
        </Card>
      );
    }

    const {
      WhatSection,
      WhatSummary,
      WhenSection,
      WhenSummary,
      showMedia,
      showFilters,
      showMessage,
      showSendTo,
      sendToPlaceholder,
    } = actionConfig;

    return (
      <AutomationCard
        automation={automation}
        cardSx={dragging ? automationDragCardSx : undefined}
        compactCard={compactCard}
        dragHandle={dragHandle}
        flatCard={Boolean(dragHandle) || compactCard || dragging}
        index={index}
        isSavingCard={
          automation.id != null
            ? savingAutomationId === automation.id
            : savingAutomationId === `unsaved-${index}`
        }
        onClone={() => handleClone(index, automation)}
        onDiscardCard={() => handleDiscardCard(index)}
        onRemove={() => {
          if (automation.id) {
            handleRemove(index, automation);
          } else {
            setFieldValue(
              'automations',
              values.automations.filter((_, currentIndex) => currentIndex !== index)
            );
          }
        }}
        onSaveCard={() => {
          void handleSaveCard(automation.id, index);
        }}
        onToggleEnabled={
          automation.id
            ? (enabled) => handleToggleEnabled(index, enabled)
            : undefined
        }
        isTogglingEnabled={togglingIndex === index}
        onUpdateAutomation={
          automation.id
            ? (patch) => updateAutomationById(automation.id!, patch)
            : undefined
        }
        renderWhatSection={() => <WhatSection automation={automation} index={index} />}
        renderWhatSummary={() => <WhatSummary automation={automation} />}
        renderWhenSection={
          WhenSection ? () => <WhenSection automation={automation} index={index} /> : undefined
        }
        renderWhenSummaryOverride={
          WhenSummary ? () => <WhenSummary automation={automation} /> : undefined
        }
        resetKey={resetKey}
        sendToPlaceholder={sendToPlaceholder}
        showFiltersSection={showFilters}
        showMediaSection={showMedia}
        showMessageSection={showMessage}
        showSendToSection={showSendTo}
      />
    );
  };

  const renderReminderById = (
    reminderId: Identifier,
    dragHandle: ReactNode,
    options?: { dragging?: boolean; grouped?: boolean }
  ) => {
    const index = findAutomationIndex(values.automations, reminderId);
    if (index === -1) {
      return null;
    }
    const automation = values.automations[index];

    return renderAutomationCard(
      automation,
      index,
      dragHandle,
      options?.grouped,
      options?.dragging
    );
  };

  const renderGroupHeader = (
    group: AutomationLayout['automationGroups'][number],
    { collapsed, onToggleCollapse }: { collapsed: boolean; onToggleCollapse: () => void }
  ) => (
    <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
      <Typography
        color="text.primary"
        sx={{ m: 0 }}
        variant="h6"
      >
        {group.name}
      </Typography>
      <AutomationGroupHeaderActions
        collapsed={collapsed}
        onRename={() => setEditingGroupId(group.id)}
        onToggleCollapse={onToggleCollapse}
      />
    </Box>
  );

  const hasSavedAutomations =
    reminders.length > 0 ||
    automationLayout.automationGroups.length > 0 ||
    unsavedAutomations.length > 0;

  if (!hasSavedAutomations) {
    return (
      <Card>
        <Box p={3} textAlign="center">
          <Typography color="text.secondary" variant="body1">
            {t('automations.page.noAutomationsFound')}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {editingGroupId ? (() => {
        const editingGroup = automationLayout.automationGroups.find(
          (group) => group.id === editingGroupId
        );
        if (!editingGroup) {
          return null;
        }

        const groupReminderIds = (editingGroup.reminders || [])
          .map((reminder) => reminder.id!)
          .filter(Boolean);
        const selectableReminders = [
          ...automationLayout.ungroupedReminders,
          ...(editingGroup.reminders || []),
        ].filter(
          (reminder, index, allReminders) =>
            allReminders.findIndex((item) => String(item.id) === String(reminder.id)) === index
        );

        return (
          <CreateAutomationGroupDialog
            key={String(editingGroupId)}
            initialValues={{
              description: editingGroup.description ?? '',
              name: editingGroup.name,
              reminderIds: groupReminderIds,
            }}
            onClose={() => setEditingGroupId(null)}
            onDelete={async () => {
              await onDeleteGroup(editingGroupId);
              setEditingGroupId(null);
            }}
            onSave={async (values) => {
              await onUpdateGroup(editingGroupId, values);
              setEditingGroupId(null);
            }}
            open
            selectableReminders={selectableReminders}
            variant="edit"
          />
        );
      })() : null}

      <AutomationLayoutDnD
        layout={automationLayout}
        onReorderFailed={handleReorderFailed}
        onReordered={handleReordered}
        renderGroupHeader={renderGroupHeader}
        renderReminder={renderReminderById}
      />

      {unsavedAutomations.length > 0 ? (
        <Box>
          <Typography fontWeight="bold" mb={2} variant="body1">
            {t('automations.groups.unsavedAutomations')}
          </Typography>
          <Stack spacing={2}>
            {unsavedAutomations.map((automation) => {
              const index = values.automations.findIndex((item) => item === automation);
              return (
                <Box key={`unsaved-${index}`}>
                  {renderAutomationCard(automation, index)}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

export default function AutomationsPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [deleteConfirmProps, confirm] = useConfirmDialogComponent();
  const formRef = useRef<FormikProps<AutomationsFormValues>>(null);
  const [resetKey, setResetKey] = useState(0);

  const {
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    defaultPerPage: 100,
  });

  const customTypeFilter = getFilterParamValue('customType') as string | undefined;

  const { data: reminders = [], isLoading: remindersLoading } = useQuery({
    queryKey: automationsQueryKey(customTypeFilter),
    queryFn: () =>
      fetchReminders({
        fetchAll: true,
        userId: session?.trainerId,
        customType: customTypeFilter || undefined,
      }),
    enabled: Boolean(session?.trainerId),
  });

  const { data: automationGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: automationGroupsQueryKey(),
    queryFn: fetchAutomationGroups,
  });

  const isLoading = remindersLoading || groupsLoading;

  const layout = useMemo(
    () => buildAutomationLayout(automationGroups, reminders),
    [automationGroups, reminders]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    patchRemindersWithMissingGroups(queryClient, automationGroups);
  }, [automationGroups, isLoading, queryClient]);

  const invalidateAutomationQueries = async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['automations'] }),
      queryClient.refetchQueries({ queryKey: automationGroupsQueryKey() }),
    ]);
  };

  const syncFormAutomationsFromQuery = () => {
    const freshReminders =
      queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
      reminders;
    formRef.current?.setFieldValue(
      'automations',
      normalizeAutomations(freshReminders)
    );
  };

  const createMutation = useMutation({
    mutationFn: (automation: Reminder) => createReminder(automation),
    onSuccess: () => {
      void invalidateAutomationQueries();
      setWizardOpen(false);
      toast.success(t('automations.page.createdSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToCreate'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (automation: Partial<Reminder>) => updateReminder(automation),
    onSuccess: (automation) => {
      patchReminderInAutomationsCache(queryClient, automation);
      toast.success(
        automation.name
          ? t('automations.page.updatedSuccessfullyNamed', { name: automation.name })
          : t('automations.page.updatedSuccessfully')
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToUpdate'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteReminder(id),
    onSuccess: () => {
      void invalidateAutomationQueries();
      toast.success(t('automations.page.deletedSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToDelete'));
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: createAutomationGroup,
    onSuccess: () => {
      void invalidateAutomationQueries();
      toast.success(t('automations.groups.createdSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.groups.failedToCreate'));
    },
  });

  const handleDeleteGroup = async (groupId: Identifier) => {
    await deleteAutomationGroup(groupId);
    patchRemindersAfterGroupDelete(queryClient, groupId);
    syncFormAutomationsFromQuery();
    toast.success(t('automations.groups.deletedSuccessfully'));
  };

  const handleUpdateGroup = async (
    groupId: Identifier,
    values: AutomationGroupDialogValues
  ) => {
    await updateAutomationGroup(groupId, {
      description: values.description,
      name: values.name,
      reminderIds: values.reminderIds,
    });
    patchRemindersAfterGroupUpdate(queryClient, groupId, values.reminderIds);
    await invalidateAutomationQueries();
    syncFormAutomationsFromQuery();
    toast.success(t('automations.groups.updatedSuccessfully'));
  };

  const handleSubmit = async (
    values: AutomationsFormValues,
    helpers: FormikHelpers<AutomationsFormValues>
  ) => {
    const updatePromises = values.automations.map((automation) => {
      const reminderData = automation as unknown as Reminder;
      if (automation.id) {
        return updateMutation.mutateAsync(reminderData);
      }
      return createMutation.mutateAsync(reminderData);
    });

    try {
      await Promise.all(updatePromises);
      await queryClient.refetchQueries({
        queryKey: automationsQueryKey(customTypeFilter),
      });
      const freshReminders =
        queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
        reminders;
      helpers.resetForm({
        values: { automations: normalizeAutomations(freshReminders) },
      });
      setResetKey((k) => k + 1);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const filterConfigs = [
    {
      field: 'customType',
      label: t('automations.page.automationType'),
      Component: SelectFilter,
      props: {
        options: AUTOMATION_TYPE_OPTIONS,
        searchable: true,
      },
    },
  ];

  return (
    <>
      <Meta brand={session?.brand} title={t('automations.page.title')} />
      <AppFrameConfig
        bgcolor="background.level1"
        actions={[
          {
            label: t('automations.page.saveAll'),
            disabled: isLoading,
            onClick: () => {
              void formRef.current?.submitForm();
            },
            variant: 'bordered',
          },
          {
            icon: faFolderPlus,
            label: t('automations.groups.createGroup'),
            onClick: () => setGroupDialogOpen(true),
            variant: 'bordered',
          },
          {
            icon: faPlus,
            label: t('automations.page.createNew'),
            onClick: () => setWizardOpen(true),
            variant: 'prominent',
          },
        ]}
        containerMaxWidth="lg"
        fixedHeader
        id="automations"
        title={t('automations.page.title')}
      />

      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <Filters
          filters={filterConfigs}
          getFilterParamValue={getFilterParamValue}
          hideMainDropdown
          params={params}
          resetFilterParams={resetFilterParams}
          updateFilterParam={updateFilterParam}
          updateParam={updateParam}
        />
      </SectionToolbar>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <Loader size="large" />
        </Box>
      ) : (
        <Formik
          enableReinitialize={false}
          initialValues={{
            automations: normalizeAutomations(reminders),
          }}
          innerRef={formRef}
          key={String(customTypeFilter ?? '')}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: formikSubmit, setFieldValue, values }) => {
            const handleWizardAdd = (data: {
              customType: string;
              reminderType: string;
              name: string;
            }) => {
              const typeDefaults = AUTOMATION_TYPE_DEFAULTS[data.customType] || {};
              const reminderType =
                data.reminderType || typeDefaults.reminderType || 'all';
              const triggerConfig = TRIGGERS[reminderType];
              const automation = {
                ...DEFAULT_AUTOMATION,
                ...typeDefaults,
                customType: data.customType,
                reminderType,
                name: data.name,
                trainerId: session?.trainerId || 0,
                createdAt: Date.now(),
                updatedAt: Date.now(),
              } as AutomationItem;

              if (triggerConfig?.delayTypes?.length) {
                automation.delayType = triggerConfig.delayTypes[0];
              }
              if (data.customType === 'client-tag' && !automation.tags) {
                automation.tags = 'add';
              }

              setFieldValue('automations', [automation, ...values.automations]);
              setWizardOpen(false);
            };

            return (
              <>
                <form onSubmit={formikSubmit}>
                  <AutomationsFormContent
                    automationLayout={layout}
                    confirm={confirm}
                    createMutation={createMutation}
                    customTypeFilter={customTypeFilter}
                    deleteMutation={deleteMutation}
                    formRef={formRef}
                    onDeleteGroup={handleDeleteGroup}
                    onUpdateGroup={handleUpdateGroup}
                    reminders={reminders}
                    resetKey={resetKey}
                    setResetKey={setResetKey}
                    updateMutation={updateMutation}
                  />
                </form>
                <CreateAutomationWizard
                  onAdd={handleWizardAdd}
                  onClose={() => setWizardOpen(false)}
                  open={wizardOpen}
                />
                <CreateAutomationGroupDialog
                  onClose={() => setGroupDialogOpen(false)}
                  onSave={async (values) => {
                    await createGroupMutation.mutateAsync(values);
                    setGroupDialogOpen(false);
                  }}
                  open={groupDialogOpen}
                  selectableReminders={layout.ungroupedReminders}
                  variant="create"
                />
              </>
            );
          }}
        </Formik>
      )}

      <ConfirmDialog {...deleteConfirmProps} />
    </>
  );
}
